import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list.
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.addEventListener(
      'click',
      this.#handlePaginationClick
    );

    this.addEventListener(
      'change',
      this.#handleProductsPerPageChange
    );

    this.#initializeCataloguePagination();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener(
      'change',
      this.#handleMediaQueryChange
    );

    this.removeEventListener(
      'click',
      this.#handlePaginationClick
    );

    this.removeEventListener(
      'change',
      this.#handleProductsPerPageChange
    );
  }

  /**
   * Updates the product-grid layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates and stores the selected grid layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(
      () => this.#setLayout(value),
      ['product-grid']
    );

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches
        ? 'desktop'
        : 'mobile';

      sessionStorage.setItem(
        `product-grid-view-${viewport}`,
        value
      );
    });
  };

  /**
   * Applies the selected grid layout.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;

    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Resets the grid when the viewport changes.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector(
          '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector(
          '[data-grid-layout="mobile-option"]'
        );

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Reads the selected page size and displays the appropriate products.
   */
  #initializeCataloguePagination() {
    const grid = this.querySelector('[ref="grid"]');
    const navigation = this.querySelector(
      '[data-catalogue-pagination]'
    );

    if (
      !(grid instanceof HTMLElement) ||
      !(navigation instanceof HTMLElement)
    ) {
      return;
    }

    const url = new URL(window.location.href);
    const supportedSizes = [12, 24, 48];

    const storedSize = Number(
      localStorage.getItem(
        'electrotk-products-per-page'
      )
    );

    const requestedSize = Number(
      url.searchParams.get('products_per_page')
    );

    let perPage = 24;

    if (supportedSizes.includes(requestedSize)) {
      perPage = requestedSize;
    } else if (supportedSizes.includes(storedSize)) {
      perPage = storedSize;
    }

    const nativePage = Math.max(
      1,
      Number(grid.dataset.nativePage) || 1
    );

    const nativePageSize = Math.max(
      1,
      Number(grid.dataset.nativePageSize) || 48
    );

    const totalProducts = Math.max(
      0,
      Number(grid.dataset.totalProducts) || 0
    );

    const totalPages = Math.max(
      1,
      Math.ceil(totalProducts / perPage)
    );

    const firstGlobalPageInBatch =
      Math.floor(
        ((nativePage - 1) * nativePageSize) / perPage
      ) + 1;

    const requestedPage = Number(
      url.searchParams.get('catalogue_page')
    );

    let currentPage = firstGlobalPageInBatch;

    if (
      Number.isFinite(requestedPage) &&
      requestedPage > 0
    ) {
      currentPage = requestedPage;
    }

    currentPage = Math.min(
      totalPages,
      Math.max(
        firstGlobalPageInBatch,
        currentPage
      )
    );

    const selectors = document.querySelectorAll(
      '[data-products-per-page]'
    );

    for (const select of selectors) {
      if (select instanceof HTMLSelectElement) {
        select.value = String(perPage);
      }
    }

    const batchStartIndex =
      (nativePage - 1) * nativePageSize;

    const pageStartIndex =
      (currentPage - 1) * perPage;

    const localStartIndex = Math.max(
      0,
      pageStartIndex - batchStartIndex
    );

    const cards = Array.from(grid.children).filter(
      (child) =>
        child.classList.contains(
          'product-grid__item'
        )
    );

    cards.forEach((card, index) => {
      const shouldHide =
        index < localStartIndex ||
        index >= localStartIndex + perPage;

      card.toggleAttribute('hidden', shouldHide);
    });

    this.#renderPagination(
      navigation,
      currentPage,
      totalPages,
      perPage,
      nativePageSize
    );
  }

  /**
   * Builds the page buttons and Previous/Next links.
   */
  #renderPagination(
    navigation,
    currentPage,
    totalPages,
    perPage,
    nativePageSize
  ) {
    const pagesContainer = navigation.querySelector(
      '[data-catalogue-pages]'
    );

    const previous = navigation.querySelector(
      '[data-catalogue-previous]'
    );

    const next = navigation.querySelector(
      '[data-catalogue-next]'
    );

    if (
      !(pagesContainer instanceof HTMLElement) ||
      !(previous instanceof HTMLAnchorElement) ||
      !(next instanceof HTMLAnchorElement)
    ) {
      return;
    }

    previous.href = this.#buildPageUrl(
      currentPage - 1,
      perPage,
      nativePageSize
    );

    next.href = this.#buildPageUrl(
      currentPage + 1,
      perPage,
      nativePageSize
    );

    previous.setAttribute(
      'aria-disabled',
      String(currentPage <= 1)
    );

    next.setAttribute(
      'aria-disabled',
      String(currentPage >= totalPages)
    );

    const visiblePages = this.#getVisiblePages(
      currentPage,
      totalPages
    );

    pagesContainer.replaceChildren();

    for (const page of visiblePages) {
      if (page === 'ellipsis') {
        const ellipsis =
          document.createElement('span');

        ellipsis.className =
          'catalogue-pagination__ellipsis';

        ellipsis.textContent = '...';

        ellipsis.setAttribute(
          'aria-hidden',
          'true'
        );

        pagesContainer.append(ellipsis);
        continue;
      }

      const link = document.createElement('a');

      link.className =
        'catalogue-pagination__page';

      link.href = this.#buildPageUrl(
        page,
        perPage,
        nativePageSize
      );

      link.textContent = String(page);
      link.dataset.cataloguePage = String(page);

      link.setAttribute(
        'aria-label',
        `Page ${page}`
      );

      if (page === currentPage) {
        link.setAttribute(
          'aria-current',
          'page'
        );
      }

      pagesContainer.append(link);
    }

    navigation.hidden = totalPages <= 1;
  }

  /**
   * Chooses the numbered pages displayed in the navigation.
   */
  #getVisiblePages(currentPage, totalPages) {
    if (totalPages <= 7) {
      return Array.from(
        { length: totalPages },
        (_, index) => index + 1
      );
    }

    const pages = [1];

    const start = Math.max(
      2,
      currentPage - 1
    );

    const end = Math.min(
      totalPages - 1,
      currentPage + 1
    );

    if (start > 2) {
      pages.push('ellipsis');
    }

    for (
      let page = start;
      page <= end;
      page += 1
    ) {
      pages.push(page);
    }

    if (end < totalPages - 1) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);

    return pages;
  }

  /**
   * Builds a URL for the requested catalogue page.
   */
  #buildPageUrl(
    cataloguePage,
    perPage,
    nativePageSize
  ) {
    const safePage = Math.max(
      1,
      cataloguePage
    );

    const globalStartIndex =
      (safePage - 1) * perPage;

    const nativePage =
      Math.floor(
        globalStartIndex / nativePageSize
      ) + 1;

    const url = new URL(
      window.location.href
    );

    url.searchParams.set(
      'products_per_page',
      String(perPage)
    );

    url.searchParams.set(
      'catalogue_page',
      String(safePage)
    );

    url.searchParams.set(
      'page',
      String(nativePage)
    );

    url.hash = 'ResultsList';

    return (
      url.pathname +
      url.search +
      url.hash
    );
  }

  /**
   * Handles Previous, Next and numbered-page clicks.
   */
  #handlePaginationClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const link = event.target.closest(
      '[data-catalogue-pagination] a'
    );

    if (
      !(link instanceof HTMLAnchorElement) ||
      link.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    const targetUrl = new URL(link.href);
    const currentUrl = new URL(
      window.location.href
    );

    const targetNativePage =
      targetUrl.searchParams.get('page');

    const currentNativePage =
      currentUrl.searchParams.get('page');

    /*
     * Allow normal navigation when Shopify needs to load
     * another native batch of products.
     */
    if (targetNativePage !== currentNativePage) {
      return;
    }

    event.preventDefault();

    window.history.pushState(
      {},
      '',
      targetUrl
    );

    this.#initializeCataloguePagination();

    const resultsList =
      this.querySelector('#ResultsList');

    if (resultsList) {
      resultsList.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  /**
   * Handles changes to the products-per-page selector.
   */
  #handleProductsPerPageChange = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const select = event.target.closest(
      '[data-products-per-page]'
    );

    if (!(select instanceof HTMLSelectElement)) {
      return;
    }

    const perPage = Number(select.value);

    if (![12, 24, 48].includes(perPage)) {
      return;
    }

    localStorage.setItem(
      'electrotk-products-per-page',
      String(perPage)
    );

    const url = new URL(
      window.location.href
    );

    url.searchParams.set(
      'products_per_page',
      String(perPage)
    );

    url.searchParams.set(
      'catalogue_page',
      '1'
    );

    url.searchParams.set(
      'page',
      '1'
    );

    url.hash = 'ResultsList';

    window.location.assign(url);
  };
}

if (!customElements.get('results-list')) {
  customElements.define(
    'results-list',
    ResultsList
  );
}